import {
  Home,
  FileText,
  Settings,
  Key,
  Download,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Building,
  ListChecks,
} from "lucide-react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import logoRedMimbre from "../assets/red-mimbre-logo-1.png";

interface SidebarProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

export default function Sidebar({
  isCollapsed,
  onToggleCollapse,
}: SidebarProps) {
  const { user, logout, translations, role } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const adminItems = [
    { id: "dashboard", icon: Home, label: translations.dashboard },
    { id: "surveys", icon: FileText, label: translations.surveys },
    { id: "questions", icon: Settings, label: translations.genericQuestions },
    {
      id: "questions-specific",
      icon: ListChecks,
      label: translations.specificQuestions,
    },
    { id: "entities", icon: Building, label: translations.entitiesNav },
    { id: "codes", icon: Key, label: translations.codes },
    { id: "export", icon: Download, label: translations.downloadData },
  ];

  const orgItems = [
    { id: "dashboard", icon: Home, label: translations.dashboard },
    { id: "surveys", icon: FileText, label: translations.surveys },
    { id: "questions", icon: Settings, label: translations.genericQuestions },
    {
      id: "questions-specific",
      icon: ListChecks,
      label: translations.specificQuestions,
    },
    { id: "codes", icon: Key, label: translations.codes },
    { id: "export", icon: Download, label: translations.downloadData },
  ];

  const menuItems = role === "Administrator" ? adminItems : orgItems;

  return (
    <div
      className={`bg-gradient-to-br from-emerald-900 via-teal-900 to-green-900 text-white h-screen flex flex-col transition-all duration-300 shadow-xl ${
        isCollapsed ? "w-16" : "w-64"
      }`}
    >
      {/* Header */}
      <div className="p-4 border-b border-emerald-800/50 flex items-center justify-between">
        {!isCollapsed ? (
          <div className="flex items-center space-x-3 flex-1">
            <img 
              src={logoRedMimbre} 
              alt="REDMIMBRE Logo" 
              className="w-16 h-16 object-contain filter drop-shadow-[0_8px_20px_rgba(0,0,0,0.3)]"
            />
          </div>
        ) : (
          <div className="flex justify-center w-full">
            <img 
              src={logoRedMimbre} 
              alt="REDMIMBRE Logo" 
              className="w-12 h-12 object-contain filter drop-shadow-[0_4px_12px_rgba(0,0,0,0.3)]"
            />
          </div>
        )}
        <button
          onClick={onToggleCollapse}
          className="p-2 rounded-lg hover:bg-emerald-800/50 transition-all duration-200 hover:scale-105"
          aria-label="Toggle sidebar"
        >
          {isCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const path = `/${item.id === "dashboard" ? "dashboard" : item.id}`;
          const isActive = location.pathname === path || (item.id === "dashboard" && location.pathname === "/");

          const handleClick = (e: React.MouseEvent) => {
            // Si estamos en la misma ruta, forzar navegación y refresco
            if (isActive) {
              e.preventDefault();
              // Invalidar queries antes de navegar solo para datos de la página actual
              if (path === "/dashboard") {
                queryClient.invalidateQueries({ queryKey: ["dashboardMetrics"], refetchType: 'active' });
                queryClient.invalidateQueries({ queryKey: ["recentActivity"], refetchType: 'active' });
              } else if (path.startsWith("/surveys")) {
                queryClient.invalidateQueries({ queryKey: ["surveys"], refetchType: 'active' });
              } else if (path.startsWith("/entities")) {
                queryClient.invalidateQueries({ queryKey: ["entities"], refetchType: 'active' });
                queryClient.invalidateQueries({ queryKey: ["users"], refetchType: 'active' });
              } else if (path === "/questions" || path === "/questions-specific") {
                queryClient.invalidateQueries({ queryKey: ["questions"], refetchType: 'active' });
              }
              // Navegar con un pequeño delay para permitir que la invalidación se procese
              setTimeout(() => {
                navigate(path, { replace: true });
              }, 100);
            }
          };

          return (
            <NavLink
              key={item.id}
              to={path}
              end={item.id === "dashboard"}
              onClick={handleClick}
              title={isCollapsed ? item.label : undefined}
              className={`w-full flex items-center px-4 py-3 text-left transition-all duration-200 group ${
                isCollapsed ? "justify-center" : ""
              } ${
                isActive
                  ? "bg-emerald-700/80 border-r-4 border-emerald-300 shadow-lg backdrop-blur-sm"
                  : "hover:bg-emerald-800/50 hover:border-r-4 hover:border-emerald-700/50"
              }`}
            >
              <Icon 
                size={20} 
                className={`flex-shrink-0 transition-colors duration-200 ${
                  isActive ? "text-emerald-100" : "text-emerald-200 group-hover:text-white"
                }`}
              />
              {!isCollapsed && (
                <span className={`ml-3 font-medium transition-colors duration-200 ${
                  isActive ? "text-white font-semibold" : "text-emerald-100 group-hover:text-white"
                }`}>
                  {item.label}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* User section */}
      <div className="p-4 border-t border-emerald-800/50 bg-emerald-950/30">
        {!isCollapsed && (
          <div className="mb-3 px-2">
            <p className="text-sm font-semibold text-white">{user?.fullName}</p>
            <p className="text-xs text-emerald-200 truncate">{user?.email}</p>
          </div>
        )}
        <button
          onClick={logout}
          className={`w-full flex items-center px-4 py-2 text-left transition-all duration-200 rounded-lg hover:bg-red-600/20 hover:text-red-300 group ${
            isCollapsed ? "justify-center" : ""
          }`}
          title={isCollapsed ? translations.logout : undefined}
        >
          <LogOut 
            size={20} 
            className="flex-shrink-0 text-emerald-200 group-hover:text-red-300 transition-colors duration-200" 
          />
          {!isCollapsed && (
            <span className="ml-3 text-emerald-200 group-hover:text-red-300 transition-colors duration-200 font-medium">
              {translations.logout}
            </span>
          )}
        </button>
      </div>
    </div>
  );
}
